import { createFileRoute, useParams } from "@tanstack/react-router";
import { CampaignDetail } from "@/components/outreach/CampaignDetail";
import { parseHubFilters } from "@/components/outreach/hub-filters";
import { OutreachWizard } from "@/components/outreach/OutreachWizard";
import { Skeleton } from "@/components/ui/skeleton";
import { useOutreachCampaign } from "@/hooks/use-outreach";
import { useSession } from "@/hooks/use-session";

export const Route = createFileRoute("/_app/_/outreach/$ocId")({
	// Hub filters are forwarded here so the detail's "Back to outreach" returns
	// to the same filtered view.
	validateSearch: parseHubFilters,
	component: OutreachCampaignPage,
});

function OutreachCampaignPage() {
	const session = useSession();
	const campaignId = session.campaign?.id ?? 0;
	const { ocId } = useParams({ from: "/_app/_/outreach/$ocId" });
	const filters = Route.useSearch();
	const oc = useOutreachCampaign(campaignId, Number(ocId));

	if (!campaignId) {
		return (
			<div className="mx-auto max-w-3xl p-6">
				<p className="text-muted-foreground">No campaign found.</p>
			</div>
		);
	}
	if (oc.isLoading) {
		return (
			<div className="mx-auto max-w-3xl space-y-4 p-6">
				<Skeleton className="h-8 w-40" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}
	if (oc.error || !oc.data) {
		return (
			<div className="mx-auto max-w-3xl p-6">
				<p className="text-destructive">Could not load this outreach campaign.</p>
			</div>
		);
	}
	// Drafts open in the wizard; every launched / terminal status opens the
	// channel-aware monitoring detail page (PRD 46 §7). Channel-id presence is
	// the reliable "has been launched" signal.
	const launched =
		oc.data.display_status !== "draft" ||
		oc.data.text_campaign_id != null ||
		oc.data.phone_campaign_id != null ||
		oc.data.email_campaign_id != null;

	if (launched) {
		return (
			<CampaignDetail
				campaignId={campaignId}
				campaign={oc.data}
				backSearch={filters}
			/>
		);
	}

	return (
		<OutreachWizard
			campaignId={campaignId}
			initial={oc.data}
			accountName={session.campaign?.name ?? undefined}
			backSearch={filters}
		/>
	);
}
